import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import { comics } from "@/mocks/comics";
import { useSeo } from "@/hooks/useSeo";

const recentChapters = [
  { no: "最新", label: "第 236 话 · 星夜决战", time: "2 小时前" },
  { no: "prev", label: "第 235 话 · 剑意通天", time: "昨天" },
  { no: "prev", label: "第 234 话 · 破茧", time: "3 天前" },
  { no: "prev", label: "第 233 话 · 逆转", time: "5 天前" },
];

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id) ?? comics[0];
  const related = comics.filter((c) => c.id !== comic.id).slice(0, 4);

  useSeo(
    `${comic.title} - 扑飞漫画网在线漫画`,
    `${comic.title}漫画由${comic.author}创作，已更新至${comic.chapters}话，支持翻页、下拉阅读、目录跳转、夜间模式与离线缓存。`
  );

  const schema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: comic.title,
    genre: comic.genre,
    author: { "@type": "Person", name: comic.author },
    about: comic.description,
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <section className="border-b border-background-200/70 bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
            <nav aria-label="面包屑" className="mb-6 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500">首页</Link>
              <span className="mx-2">/</span>
              <Link to="/comics" className="hover:text-primary-500">漫画库</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-800">{comic.title}</span>
            </nav>

            <script type="application/ld+json">{JSON.stringify(schema)}</script>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-[280px_1fr]">
              <div className="mx-auto w-48 md:mx-0 md:w-full">
                <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={`${comic.title}漫画封面`}
                    title={`${comic.title} · 扑飞漫画网`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">{comic.title}</h1>
                  <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                    {comic.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                  <span>作者：{comic.author}</span>
                  <span>题材：{comic.genre}</span>
                  <span>已更 {comic.chapters} 话</span>
                  <span className="flex items-center gap-1 text-primary-600">
                    <i className="ri-star-fill text-sm" /> {comic.rating}
                  </span>
                  <span>{comic.readers} 读者在追</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {comic.tags.map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                      {t}
                    </span>
                  ))}
                </div>

                <p className="mt-6 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                  {comic.description}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    开始阅读 <i className="ri-play-circle-line" />
                  </button>
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    <i className="ri-download-cloud-line" /> 缓存全本
                  </button>
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-6 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-heart-line" /> 加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">目录 · 最近更新</h2>
              <ul className="mt-6 divide-y divide-background-200/70 border-y border-background-200/70">
                {recentChapters.map((c) => (
                  <li key={c.label}>
                    <a
                      href="#"
                      rel="nofollow"
                      className="flex items-center justify-between gap-3 py-3.5 text-sm transition-colors hover:text-primary-600"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${
                            c.no === "最新"
                              ? "bg-primary-500 text-background-50"
                              : "bg-secondary-100 text-secondary-700"
                          }`}
                        >
                          {c.no === "最新" ? "新" : c.no === "prev" ? <i className="ri-play-fill" /> : c.no}
                        </span>
                        {c.label}
                      </span>
                      <span className="shrink-0 text-xs text-foreground-500">{c.time}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-foreground-500">
                完整目录共 {comic.chapters} 话，支持目录一键跳转与离线缓存，随时随地追更。
              </p>
            </div>

            <aside className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h3 className="font-heading text-base text-foreground-950">阅读小贴士</h3>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-primary-500" /> 可切换翻页 / 下拉阅读
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-primary-500" /> 夜间模式护眼不刺眼
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-primary-500" /> 缓存全本离线也能看
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-primary-500" /> 横屏阅读大场面更震撼
                </li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="border-t border-background-200/70 bg-background-100/70 py-10 md:py-14">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">相关推荐</h2>
            <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-4">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}